import { Button, Input } from '@chakra-ui/react';
import { isAxiosError } from 'axios';
import type { SubmitEvent } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { axiosInstance } from '@/api/axiosInstance';
import { flattenApiErrors } from '@/api/errors';
import { Alert, Link, VStack } from '@/components';

import { resetPasswordCss } from './ResetPassword.styles';

const RESET_FAILED = 'Failed to reset password. The link may have expired.';

const resetErrorMessage = (error: unknown) => {
  if (!isAxiosError(error) || error.response?.status !== 400)
    return RESET_FAILED;
  const data: unknown = error.response.data;
  if (!data || typeof data !== 'object') return RESET_FAILED;
  const messages = flattenApiErrors(Object.values(data));
  return messages.length ? messages.join(' ') : RESET_FAILED;
};

export const ResetPassword = () => {
  const navigate = useNavigate();
  const { uidb64, token } = useParams<{ uidb64: string; token: string }>();
  const hasParams = Boolean(uidb64 && token);
  const [error, setError] = useState<string | null>(
    hasParams ? null : 'Invalid or missing reset token.',
  );
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isValidating, setIsValidating] = useState(hasParams);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!uidb64 || !token) return;

    axiosInstance
      .get(`/auth/password-reset/${uidb64}/${token}/`)
      .then(() => {
        setIsTokenValid(true);
      })
      .catch(() => {
        setError('This reset link is invalid or has expired.');
      })
      .finally(() => {
        setIsValidating(false);
      });
  }, [uidb64, token]);

  const handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const form = event.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const new_password1 = formData.get('password') as string;
    const new_password2 = formData.get('confirmPassword') as string;

    if (new_password1 !== new_password2) {
      setError('Passwords do not match.');
      setIsSubmitting(false);
      return;
    }

    try {
      await axiosInstance.post(`/auth/password-reset/${uidb64}/${token}/`, {
        new_password1,
        new_password2,
      });
      navigate('../login');
    } catch (caught) {
      setError(resetErrorMessage(caught));
      setIsSubmitting(false);
    }
  };

  const disabled = !isTokenValid || isValidating;

  return (
    <VStack
      as="form"
      className="ResetPassword"
      css={resetPasswordCss}
      onSubmit={handleSubmit}
    >
      {error && <Alert status="error" variant="solid" title={error} />}

      <Input
        name="password"
        aria-label="password"
        placeholder="New password"
        type="password"
        autoComplete="new-password"
        required
        disabled={disabled}
      />
      <Input
        name="confirmPassword"
        aria-label="confirm new password"
        placeholder="Confirm new password"
        type="password"
        autoComplete="new-password"
        required
        disabled={disabled}
      />

      <Button
        type="submit"
        colorPalette="accent"
        pill
        width="full"
        disabled={disabled}
        loading={isSubmitting || isValidating}
      >
        Reset password
      </Button>

      <Link to="../login" variant="underline" className="back-link">
        Back to Login
      </Link>
    </VStack>
  );
};
